import { Box, Checkbox, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { CardWithContent } from 'src/components/molecules/card-with-content';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { CompactPagination } from 'src/components/organisms/compact-pagination';
import { useAssignSpacesToSpaceGroup } from 'src/features/space-groups/api/hooks/use-assign-spaces-to-space-group';
import { fetchSpaces } from 'src/features/spaces/api/lib/spaces';
import { spacesKeys } from 'src/features/spaces/api/query-keys/spaces';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { SearchState } from 'src/hooks/meilisearch/types/search-state';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { usePagination } from 'src/hooks/use-pagination';
import { useSearchState } from 'src/hooks/use-search-state';
import { useUserStore } from 'src/store/user-store';
import { SpaceFloorFilterPopover } from '../../molecules/space-floor-filter-popover';
import { SpaceTypeFilterPopover } from '../../molecules/space-type-filter-popover';

interface Props extends DialogActions {
  spaceGroupToken: string;
}

export const AssignSpaceToSpaceGroupDialog = ({
  open,
  onCancel,
  spaceGroupToken,
}: Props) => {
  const { t } = useTranslation(['spaceGroups', 'spaces']);

  const { directoryToken } = useUserStore();

  const initialFilterValue = [
    'spaceGroupToken NOT EXISTS OR spaceGroupToken IS NULL',
  ];

  const [searchState, setSearchState] = useSearchState({
    filter: initialFilterValue,
  });

  const {
    page,
    perPage,
    changePage,
    changePerPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const { results, isLoading, isFetching, error } = useMeilisearch({
    fetch: fetchSpaces,
    page,
    perPage,
    queryKey: spacesKeys.list(directoryToken ?? '', perPage, page, searchState),
    searchState,
    enabled: open,
  });

  useEffect(() => {
    if (!results) return;
    setTotalItems(results.estimatedTotalHits);
  }, [results]);

  const [selectedSpaceTokens, setSelectedSpaceTokens] = useState<string[]>([]);

  const handleCancel = () => {
    onCancel?.();
    setSelectedSpaceTokens([]);
    setSearchState({
      q: '',
      filter: [initialFilterValue],
      sort: [],
    });
  };

  const {
    mutate: assignSpacesToSpaceGroup,
    isPending: isAssigningSpacesToSpaceGroup,
  } = useAssignSpacesToSpaceGroup(handleCancel);

  const handleChangeQuery = (query: string) => {
    setSearchState(prevState => ({
      ...prevState,
      q: query,
    }));
  };

  const handleChangeFilterValue = (filters: SearchState['filter']) => {
    setSearchState(prevState => ({
      ...prevState,
      filter: [[...initialFilterValue], ...filters],
    }));
  };

  const handleUpdateSelectedSpaces = (token: string) => {
    if (selectedSpaceTokens.includes(token)) {
      setSelectedSpaceTokens(prevSelectedTokens =>
        prevSelectedTokens.filter(spaceToken => spaceToken !== token),
      );

      return;
    }

    setSelectedSpaceTokens(prevSelectedTokens => [
      ...prevSelectedTokens,
      token,
    ]);
  };

  const handleAssignSpacesToSpaceGroup = async () => {
    assignSpacesToSpaceGroup({
      spaceGroupToken: spaceGroupToken,
      spacesTokens: selectedSpaceTokens,
    });
  };

  return (
    <Dialog
      open={open}
      title={t('spaceGroups:details.assignSpacesToSpaceGroupDialog.title')}
      onCancel={handleCancel}
      onConfirm={handleAssignSpacesToSpaceGroup}
      isConfirmButtonSubmitting={isAssigningSpacesToSpaceGroup}
      isConfirmButtonDisabled={selectedSpaceTokens.length === 0}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2">
          {t('spaceGroups:details.assignSpacesToSpaceGroupDialog.message')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={searchState.q}
          />

          <SpaceTypeFilterPopover
            filters={searchState.filter}
            onChange={handleChangeFilterValue}
          />

          <SpaceFloorFilterPopover
            filters={searchState.filter}
            onChange={handleChangeFilterValue}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          {results?.hits?.map(space => (
            <CardWithContent
              key={space.token}
              isSelected={selectedSpaceTokens.includes(space.token)}
              onClick={() => {
                handleUpdateSelectedSpaces(space.token);
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox checked={selectedSpaceTokens.includes(space.token)} />
                <Box>
                  <Typography variant="body2">{space.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t(
                      'spaceGroups:details.assignSpacesToSpaceGroupDialog.spaceDescription',
                      {
                        spaceType: t(spaceTypesTranslations[space.type]),
                        floor: space.floor,
                      },
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardWithContent>
          ))}

          <DataStatusIndicator
            dataLength={results?.hits?.length ?? 0}
            isFetching={isLoading}
            error={error?.response?.data?.message}
            resourceUnavailableText={t(
              'spaceGroups:details.assignSpacesToSpaceGroupDialog.noSpaces',
            )}
          />

          <CompactPagination
            disabled={isLoading}
            isFetching={isFetching}
            page={page}
            changePage={changePage}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
            perPage={perPage}
            totalPages={totalPages}
          />
        </Box>
      </Box>
    </Dialog>
  );
};
